<template>
  <app-layout>
    <div class="sc-container">
      <!-- Alert messages -->
      <div v-if="successMsg" class="sc-alert sc-alert-success">{{ successMsg }}</div>
      <div v-if="errorMsg" class="sc-alert sc-alert-error">{{ errorMsg }}</div>

      <div class="detail-layout">
        <!-- MAIN COLUMN -->
        <div class="detail-main">

          <!-- Title + meta bar -->
          <div class="sc-card">
            <div class="sc-card-body title-bar">
              <div class="title-bar-left">
                <h1 class="detail-title">{{ details.name }}</h1>
                <div class="detail-meta">
                  <span v-if="details.status == 1" class="status-badge badge-success">Completed</span>
                  <span v-else class="status-badge badge-warning">On-going</span>
                  <span v-if="details.city || details.state || details.country" class="meta-loc">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {{ details.city ? details.city.name + ', ' : '' }}{{ details.state ? details.state.name + ', ' : '' }}{{ details.country ? details.country.name : '' }}
                  </span>
                  <span class="meta-date">{{ formatDate(details.created_at) }}</span>
                </div>
              </div>
              <div class="title-bar-right">
                <button
                  v-if="details.user && (details.user.id == id || details.user.owner_id == id)"
                  class="btn-outline"
                  @click="showEditModal = true"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit
                </button>
                <button
                  v-if="details.user && (details.user.id == id || details.user.owner_id == id)"
                  class="btn-danger"
                  @click="confirmDelete = true"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                  Delete
                </button>
                <button
                  v-if="details.user && details.user_id != id && details.user.owner_id != id"
                  class="btn-primary"
                  @click="showConnectModal = true"
                >
                  Connect to Project
                </button>
              </div>
            </div>
          </div>

          <!-- Gallery -->
          <div v-if="details.images && details.images.length > 0">
            <div class="gallery-hero" @click="showImg(activeImageIndex)">
              <img :src="details.images[activeImageIndex].path" :alt="details.name" />
              <div class="gallery-overlay-hint">Click to expand</div>
            </div>
            <div class="gallery-thumbs">
              <div
                v-for="(img, i) in details.images"
                :key="img.id"
                class="gallery-thumb"
                :class="{ active: i === activeImageIndex }"
                @click="activeImageIndex = i"
              >
                <img :src="img.path" :alt="details.name" />
              </div>
            </div>
          </div>
          <div v-else class="gallery-placeholder">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            <span>No images available</span>
          </div>

          <vue-easy-lightbox
            :visible="visible"
            :imgs="details.images ? details.images.map(i => i.path) : []"
            :index="activeImageIndex"
            @hide="handleHide"
          />

          <!-- Tabs + actions row -->
          <div class="sc-card pd-tabs-bar">
            <div class="filter-tabs">
              <button class="filter-tab" :class="{ active: activeTab === 'overview' }" @click="activeTab = 'overview'">Overview</button>
              <button class="filter-tab" :class="{ active: activeTab === 'team' }" @click="activeTab = 'team'">Team</button>
              <button class="filter-tab" :class="{ active: activeTab === 'comments' }" @click="activeTab = 'comments'">
                Comments
                <span v-if="details.comments && details.comments.length" class="pd-tab-count">{{ details.comments.length }}</span>
              </button>
            </div>
            <div class="pd-actions-bar">
              <div class="pd-likes-wrap">
                <button
                  class="pd-like-btn"
                  :class="{ 'pd-like-btn--liked': alreadyLiked }"
                  :disabled="likeLoading || alreadyLiked"
                  @click="likeProject"
                  :title="alreadyLiked ? 'You liked this' : 'Like this project'"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" :fill="alreadyLiked ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  <span>{{ details.likes ? details.likes.length : 0 }}</span>
                </button>
                <button v-if="details.likes && details.likes.length" class="pd-likers-btn" @click="showLikersModal = true" title="See who liked this">
                  <span class="pd-likers-stack">
                    <img
                      v-for="(l, i) in details.likes.slice(0, 4)"
                      :key="i"
                      :src="l.user && l.user.avatar ? $avatarSrc(l.user.avatar, 'users') : initAvatar(l.user ? l.user.name : '')"
                      class="pd-likers-avatar"
                      :style="{ zIndex: 4 - i }"
                    />
                  </span>
                  <span v-if="details.likes.length > 4" class="pd-likers-more">+{{ details.likes.length - 4 }}</span>
                </button>
              </div>
            </div>

            <!-- Likers modal -->
            <transition name="app-drop">
              <div v-if="showLikersModal" class="likers-overlay" @click.self="showLikersModal = false">
                <div class="likers-modal">
                  <div class="likers-modal__header">
                    <span>{{ details.likes.length }} {{ details.likes.length === 1 ? 'Like' : 'Likes' }}</span>
                    <button class="likers-modal__close" @click="showLikersModal = false">
                      <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                  <div class="likers-modal__body">
                    <router-link
                      v-for="l in details.likes"
                      :key="l.id"
                      :to="l.user ? '/sc/profile/' + l.user.slug : '#'"
                      class="likers-item"
                      @click="showLikersModal = false"
                    >
                      <img
                        :src="l.user && l.user.avatar ? $avatarSrc(l.user.avatar, 'users') : initAvatar(l.user ? l.user.name : '?')"
                        class="likers-item__avatar"
                        @error="e => e.target.src = initAvatar(l.user ? l.user.name : '?')"
                      />
                      <div class="likers-item__info">
                        <span class="likers-item__name">{{ l.user ? l.user.name : 'Unknown' }}</span>
                        <span v-if="l.user && l.user.profession" class="likers-item__role">{{ l.user.profession }}</span>
                      </div>
                    </router-link>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Overview Tab -->
          <div v-if="activeTab === 'overview'">
            <div class="sc-card">
              <div class="sc-card-header">Description</div>
              <div class="sc-card-body prose-content" v-html="details.description"></div>
            </div>
            <div v-if="youtubeVideoId" class="sc-card">
              <div class="sc-card-header">Video</div>
              <div class="sc-card-body">
                <div class="sc-video-wrap">
                  <iframe
                    :src="'https://www.youtube.com/embed/' + youtubeVideoId"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                    class="sc-video-frame"
                  ></iframe>
                </div>
              </div>
            </div>

            <div v-if="instagramShortcode" class="sc-card">
              <div class="sc-card-header">Instagram</div>
              <div class="sc-card-body">
                <div class="sc-video-wrap sc-video-wrap--instagram">
                  <iframe
                    :src="'https://www.instagram.com/p/' + instagramShortcode + '/embed'"
                    frameborder="0"
                    scrolling="no"
                    allowtransparency="true"
                    class="sc-video-frame"
                  ></iframe>
                </div>
              </div>
            </div>

            <div class="sc-card">
              <div class="sc-card-header">Project Details</div>
              <div class="sc-card-body">
                <div class="details-grid">
                  <div class="detail-row">
                    <span class="detail-label">Location</span>
                    <span class="detail-value">{{ details.city ? details.city.name + ', ' : '' }}{{ details.state ? details.state.name + ', ' : '' }}{{ details.country ? details.country.name : '--' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Address</span>
                    <span class="detail-value">{{ details.address || '--' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Date Commenced</span>
                    <span class="detail-value">{{ details.project_start_date ? formatDate2(details.project_start_date) : '--' }}</span>
                  </div>
                  <div v-if="details.status == 1" class="detail-row">
                    <span class="detail-label">Date Completed</span>
                    <span class="detail-value">{{ details.project_end_date ? formatDate2(details.project_end_date) : '--' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Height</span>
                    <span class="detail-value">{{ details.height || '--' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Status</span>
                    <span class="detail-value">
                      <span v-if="details.status == 1" class="status-badge badge-success">Completed</span>
                      <span v-else class="status-badge badge-warning">On-going</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Team Tab -->
          <div v-if="activeTab === 'team'" class="sc-card">
            <div class="sc-card-header">Project Teams</div>
            <div class="sc-card-body">
              <!-- Team member component helper rendered inline -->
              <div v-if="details.getclient" class="team-member-row">
                <div class="team-role">Client / Developer</div>
                <div class="team-person">
                  <router-link :to="details.getclient.type === 'user' ? '/sc/profile/'+details.getclient.slug : '/company/'+details.getclient.slug">
                    <img v-if="details.getclient.avatar" :src="image_url+'users/'+details.getclient.avatar" class="team-avatar" alt="" />
                    <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
                    <span class="team-name">{{ details.getclient.name }}</span>
                  </router-link>
                  <span class="team-sub">{{ details.getclient.profession }}</span>
                </div>
                <div v-if="details.architectteam && details.architectteam.length" class="team-overlap" @click="viewTeam(details.architectteam, 'Client Team Members')">
                  <img v-for="m in details.architectteam.slice(0,4)" :key="m.id" :src="m.user.avatar ? image_url+'users/'+m.user.avatar : '/assets/avatar.png'" class="team-overlap-img" :alt="m.user.name" />
                  <span class="team-count">{{ details.architectteam.length }}</span>
                </div>
              </div>

              <div v-if="details.getarchitect" class="team-member-row">
                <div class="team-role">Project Architect</div>
                <div class="team-person">
                  <router-link :to="details.getarchitect.type === 'user' ? '/sc/profile/'+details.getarchitect.slug : '/company/'+details.getarchitect.slug">
                    <img v-if="details.getarchitect.avatar" :src="image_url+'users/'+details.getarchitect.avatar" class="team-avatar" alt="" />
                    <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
                    <span class="team-name">{{ details.getarchitect.name }}</span>
                  </router-link>
                  <span class="team-sub">{{ details.getarchitect.profession }}</span>
                </div>
                <div v-if="details.architectteam && details.architectteam.length" class="team-overlap" @click="viewTeam(details.architectteam, 'Architect Team Members')">
                  <img v-for="m in details.architectteam.slice(0,4)" :key="m.id" :src="m.user.avatar ? image_url+'users/'+m.user.avatar : '/assets/avatar.png'" class="team-overlap-img" :alt="m.user.name" />
                  <span class="team-count">{{ details.architectteam.length }}</span>
                </div>
              </div>

              <div v-if="details.structuralengineer" class="team-member-row">
                <div class="team-role">Structural Engineer</div>
                <div class="team-person">
                  <router-link :to="details.structuralengineer.type === 'user' ? '/sc/profile/'+details.structuralengineer.slug : '/company/'+details.structuralengineer.slug">
                    <img v-if="details.structuralengineer.avatar" :src="image_url+'users/'+details.structuralengineer.avatar" class="team-avatar" alt="" />
                    <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
                    <span class="team-name">{{ details.structuralengineer.name }}</span>
                  </router-link>
                  <span class="team-sub">{{ details.structuralengineer.profession }}</span>
                </div>
                <div v-if="details.structuralengineerteam && details.structuralengineerteam.length" class="team-overlap" @click="viewTeam(details.structuralengineerteam, 'Structural Engineer Team Members')">
                  <img v-for="m in details.structuralengineerteam.slice(0,4)" :key="m.id" :src="m.user.avatar ? image_url+'users/'+m.user.avatar : '/assets/avatar.png'" class="team-overlap-img" :alt="m.user.name" />
                  <span class="team-count">{{ details.structuralengineerteam.length }}</span>
                </div>
              </div>

              <div v-if="details.mepengineer" class="team-member-row">
                <div class="team-role">MEP Engineer</div>
                <div class="team-person">
                  <router-link :to="details.mepengineer.type === 'user' ? '/sc/profile/'+details.mepengineer.slug : '/company/'+details.mepengineer.slug">
                    <img v-if="details.mepengineer.avatar" :src="image_url+'users/'+details.mepengineer.avatar" class="team-avatar" alt="" />
                    <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
                    <span class="team-name">{{ details.mepengineer.name }}</span>
                  </router-link>
                  <span class="team-sub">{{ details.mepengineer.profession }}</span>
                </div>
              </div>

              <div v-if="details.maincontractor" class="team-member-row">
                <div class="team-role">Main Contractor</div>
                <div class="team-person">
                  <router-link :to="details.maincontractor.type === 'user' ? '/sc/profile/'+details.maincontractor.slug : '/company/'+details.maincontractor.slug">
                    <img v-if="details.maincontractor.avatar" :src="image_url+'users/'+details.maincontractor.avatar" class="team-avatar" alt="" />
                    <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
                    <span class="team-name">{{ details.maincontractor.name }}</span>
                  </router-link>
                </div>
              </div>

              <div v-if="details.quantitysurveyor" class="team-member-row">
                <div class="team-role">Quantity Surveyor</div>
                <div class="team-person">
                  <router-link :to="details.quantitysurveyor.type === 'user' ? '/sc/profile/'+details.quantitysurveyor.slug : '/company/'+details.quantitysurveyor.slug">
                    <img v-if="details.quantitysurveyor.avatar" :src="image_url+'users/'+details.quantitysurveyor.avatar" class="team-avatar" alt="" />
                    <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
                    <span class="team-name">{{ details.quantitysurveyor.name }}</span>
                  </router-link>
                </div>
              </div>

              <div v-if="details.mechanicalcontractor" class="team-member-row">
                <div class="team-role">Mechanical Contractor</div>
                <div class="team-person">
                  <router-link :to="details.mechanicalcontractor.type === 'user' ? '/sc/profile/'+details.mechanicalcontractor.slug : '/company/'+details.mechanicalcontractor.slug">
                    <img v-if="details.mechanicalcontractor.avatar" :src="image_url+'users/'+details.mechanicalcontractor.avatar" class="team-avatar" alt="" />
                    <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
                    <span class="team-name">{{ details.mechanicalcontractor.name }}</span>
                  </router-link>
                </div>
              </div>

              <div v-if="details.electricalcontractor" class="team-member-row">
                <div class="team-role">Electrical Contractor</div>
                <div class="team-person">
                  <router-link :to="details.electricalcontractor.type === 'user' ? '/sc/profile/'+details.electricalcontractor.slug : '/company/'+details.electricalcontractor.slug">
                    <img v-if="details.electricalcontractor.avatar" :src="image_url+'users/'+details.electricalcontractor.avatar" class="team-avatar" alt="" />
                    <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
                    <span class="team-name">{{ details.electricalcontractor.name }}</span>
                  </router-link>
                </div>
              </div>

              <div v-if="details.pilingcontractor" class="team-member-row">
                <div class="team-role">Piling Contractor</div>
                <div class="team-person">
                  <router-link :to="details.pilingcontractor.type === 'user' ? '/sc/profile/'+details.pilingcontractor.slug : '/company/'+details.pilingcontractor.slug">
                    <img v-if="details.pilingcontractor.avatar" :src="image_url+'users/'+details.pilingcontractor.avatar" class="team-avatar" alt="" />
                    <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
                    <span class="team-name">{{ details.pilingcontractor.name }}</span>
                  </router-link>
                </div>
              </div>

              <div v-if="details.liftcontractor" class="team-member-row">
                <div class="team-role">Lift Contractor</div>
                <div class="team-person">
                  <router-link :to="details.liftcontractor.type === 'user' ? '/sc/profile/'+details.liftcontractor.slug : '/company/'+details.liftcontractor.slug">
                    <img v-if="details.liftcontractor.avatar" :src="image_url+'users/'+details.liftcontractor.avatar" class="team-avatar" alt="" />
                    <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
                    <span class="team-name">{{ details.liftcontractor.name }}</span>
                  </router-link>
                </div>
              </div>

              <div v-if="details.projectmanager" class="team-member-row">
                <div class="team-role">Project Manager</div>
                <div class="team-person">
                  <router-link :to="details.projectmanager.type === 'user' ? '/sc/profile/'+details.projectmanager.slug : '/company/'+details.projectmanager.slug">
                    <img v-if="details.projectmanager.avatar" :src="image_url+'users/'+details.projectmanager.avatar" class="team-avatar" alt="" />
                    <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
                    <span class="team-name">{{ details.projectmanager.name }}</span>
                  </router-link>
                </div>
              </div>

              <p v-if="!details.getarchitect && !details.structuralengineer && !details.mepengineer && !details.maincontractor && !details.getclient" class="no-content">No team members listed yet.</p>
            </div>
          </div>

          <!-- Comments Tab -->
          <div v-if="activeTab === 'comments'" class="sc-card">
            <div class="sc-card-header">Comments</div>
            <div class="sc-card-body">
              <!-- Add comment -->
              <div class="comment-compose">
                <textarea
                  v-model="newComment"
                  placeholder="Add a comment..."
                  class="comment-textarea"
                  rows="3"
                ></textarea>
                <button class="btn-primary" :disabled="loading" @click="sendSubmitComment(details.id)">
                  <span v-if="loading" class="sc-spinner"></span>
                  <span v-else>Post</span>
                </button>
              </div>

              <!-- Comment list -->
              <div v-if="details.comments && details.comments.length">
                <div v-for="com in details.comments" :key="com.id" class="comment-item">
                  <div class="comment-avatar">
                    <img v-if="com.user && com.user.avatar" :src="image_url+'users/'+com.user.avatar" :alt="com.user.name" />
                    <img v-else src="/assets/avatar.png" alt="" />
                  </div>
                  <div class="comment-body">
                    <div class="comment-header-row">
                      <router-link v-if="com.user" class="comment-author" :to="'/sc/profile/'+com.user.slug">{{ com.user.name }}</router-link>
                      <span class="comment-date">{{ formatDate(com.created_at) }}</span>
                      <div v-if="com.user && com.user.id == id" class="comment-actions-menu">
                        <button class="comment-action-btn" @click="editProjectComment(com.id, com.comment)">Edit</button>
                        <button class="comment-action-btn danger" @click="deleteComment(com.id)">Delete</button>
                      </div>
                    </div>
                    <p class="comment-text">{{ com.comment }}</p>
                    <div class="comment-actions">
                      <button class="comment-action-btn">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                        {{ com.likes ? com.likes.length : 0 }}
                      </button>
                      <button v-if="checkIfUser(com.likes)" class="comment-action-btn" @click="unlikeProjectComment(com.id)">Unlike</button>
                      <button v-else class="comment-action-btn" @click="likeProjectComment(com.id)">Like</button>
                      <button class="comment-action-btn" @click="replyProjectCommentModal(com.id)">Reply</button>
                    </div>

                    <!-- Replies -->
                    <div v-if="com.sub && com.sub.length" class="replies-list">
                      <div v-for="sub in com.sub" :key="sub.id" class="comment-item reply-item">
                        <div class="comment-avatar">
                          <img v-if="sub.user && sub.user.avatar" :src="image_url+'users/'+sub.user.avatar" :alt="sub.user.name" />
                          <img v-else src="/assets/avatar.png" alt="" />
                        </div>
                        <div class="comment-body">
                          <div class="comment-header-row">
                            <router-link v-if="sub.user" class="comment-author" :to="'/sc/profile/'+sub.user.slug">{{ sub.user.name }}</router-link>
                            <span class="comment-date">{{ formatDate(sub.created_at) }}</span>
                            <div v-if="sub.user && sub.user.id == id" class="comment-actions-menu">
                              <button class="comment-action-btn" @click="editProjectComment(sub.id, sub.comment)">Edit</button>
                              <button class="comment-action-btn danger" @click="deleteComment(sub.id)">Delete</button>
                            </div>
                          </div>
                          <p class="comment-text">{{ sub.comment }}</p>
                          <div class="comment-actions">
                            <button class="comment-action-btn">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                              {{ sub.likes ? sub.likes.length : 0 }}
                            </button>
                            <button v-if="checkIfUser(sub.likes)" class="comment-action-btn" @click="unlikeProjectComment(sub.id)">Unlike</button>
                            <button v-else class="comment-action-btn" @click="likeProjectComment(sub.id)">Like</button>
                            <button class="comment-action-btn" @click="replyProjectCommentModal(sub.id)">Reply</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p v-else class="no-content">No comments yet. Be the first to comment.</p>
            </div>
          </div>

          <!-- Related Projects -->
          <div v-if="related_project.length > 0">
            <div class="section-heading">Related Projects</div>
            <div class="related-grid">
              <div v-for="(data, i) in related_project" :key="i">
                <div v-if="data.project" class="listing-card">
                  <a :href="'/project/'+data.project.slug">
                    <div class="listing-card-img">
                      <img v-if="data.project.images && data.project.images.length" :src="data.project.images[0].path" :alt="data.project.name" />
                    </div>
                  </a>
                  <div class="card-body">
                    <span v-if="data.interest" class="tag-badge">{{ data.interest.name }}</span>
                    <a :href="'/project/'+data.project.slug"><h3 class="listing-title">{{ data.project.name }}</h3></a>
                  </div>
                </div>
              </div>
            </div>
            <div class="infinite-wrapper">
              <infinite-loading force-use-infinite-wrapper=".infinite-wrapper" @infinite="handleLoadMore"></infinite-loading>
            </div>
          </div>

        </div>

        <!-- ASIDE COLUMN -->
        <aside class="detail-aside">

          <!-- Author card -->
          <div v-if="details.user" class="author-card">
            <div class="author-avatar">
              <img v-if="details.user.avatar" :src="image_url+'users/'+details.user.avatar" :alt="details.user.name" />
              <img v-else src="/assets/avatar.png" alt="" />
            </div>
            <div class="author-name">{{ details.user.name }}</div>
            <div class="author-role">
              <template v-if="details.user.currentcompany">
                {{ details.user.current_position }} at {{ details.user.currentcompany.name }}
              </template>
              <template v-else>{{ details.user.profession }}</template>
            </div>
            <div class="author-actions">
              <a
                :href="details.user.type === 'company' ? '/company/'+details.user.company.slug : '/sc/profile/'+details.user.slug"
                class="btn-outline"
                target="_blank"
              >View Profile</a>
            </div>
          </div>

          <!-- Vendors -->
          <div v-if="details.vendors && details.vendors.length" class="sc-card">
            <div class="sc-card-header">Vendors</div>
            <div class="sc-card-body">
              <div v-for="data in details.vendors" :key="data.id" class="vendor-row">
                <router-link :to="data.user.type === 'user' ? '/sc/profile/'+data.user.slug : '/company/'+data.user.slug">
                  <img v-if="data.user.avatar" :src="image_url+'users/'+data.user.avatar" class="vendor-avatar" alt="" />
                  <img v-else src="/assets/avatar.png" class="vendor-avatar" alt="" />
                </router-link>
                <div>
                  <div class="vendor-name">{{ data.user.name }}</div>
                  <div class="vendor-cat">{{ data.category ? data.category.name : '' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Ads -->
          <div v-if="adverts.length" class="sc-card">
            <div class="sc-card-body">
              <div v-for="ad in adverts" :key="ad.id" class="ad-card">
                <a :href="ad.link" target="_blank">
                  <img :src="image_url+'advert/'+ad.image" :alt="ad.link" class="ad-img" />
                </a>
              </div>
            </div>
          </div>

        </aside>
      </div>
    </div>

    <!-- Team view modal -->
    <div v-if="showTeamModal" class="modal-overlay" @click.self="showTeamModal = false">
      <div class="modal-panel">
        <div class="modal-header">
          <h2>{{ titleTeam }}</h2>
          <button class="modal-close" @click="showTeamModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div v-for="data in currentData" :key="data.id" class="team-modal-row">
            <img v-if="data.user && data.user.avatar" :src="image_url+'users/'+data.user.avatar" class="team-avatar" :alt="data.user.name" />
            <img v-else src="/assets/avatar.png" class="team-avatar" alt="" />
            <div>
              <router-link :to="data.user && data.user.type === 'user' ? '/sc/profile/'+data.user.slug : '/company/'+data.user.slug">{{ data.user ? data.user.name : '' }}</router-link>
              <div class="team-sub">{{ data.user ? data.user.profession : '' }}</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="showTeamModal = false">Close</button>
        </div>
      </div>
    </div>

    <!-- Connect to project modal -->
    <div v-if="showConnectModal" class="modal-overlay" @click.self="showConnectModal = false">
      <div class="modal-panel" style="max-width:480px">
        <div class="modal-header">
          <h2>Connect to this Project</h2>
          <button class="modal-close" @click="showConnectModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="sc-field">
            <label>Role Played</label>
            <select v-model="connection.connection_type">
              <option value="" disabled>Select role</option>
              <option value="architect">Architect</option>
              <option value="structural_engineer">Structural Engineer</option>
              <option value="mep_engineer">MEP Engineer</option>
              <option value="quantity_surveyor">Quantity Surveyor</option>
              <option value="main_contractor">Main Contractor</option>
              <option value="mechanical_contractor">Mechanical Contractor</option>
              <option value="electrical_contractor">Electrical Contractor</option>
              <option value="piling_contractor">Piling Contractor</option>
              <option value="lift_contractor">Lift Contractor</option>
              <option value="project_manager">Project Manager</option>
              <option value="vendor">Vendor</option>
            </select>
          </div>
          <div v-if="connection.connection_type === 'vendor'" class="sc-field">
            <label>Vendor Category</label>
            <select v-model="connection.vendor_category">
              <option value="" disabled>Select category</option>
              <option v-for="data in vendor_category" :key="data.id" :value="data.id">{{ data.name }}</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="showConnectModal = false">Cancel</button>
          <button class="btn-primary" :disabled="loading" @click="connectProject()">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Send Connection</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Edit comment modal -->
    <div v-if="showEditCommentModal" class="modal-overlay" @click.self="showEditCommentModal = false">
      <div class="modal-panel" style="max-width:480px">
        <div class="modal-header">
          <h2>Edit Comment</h2>
          <button class="modal-close" @click="closeEditPostCommentModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="sc-field">
            <label>Comment</label>
            <textarea v-model="edit_comment" rows="5"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="closeEditPostCommentModal">Cancel</button>
          <button class="btn-primary" :disabled="loading" @click="editComment()">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Update</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Reply comment modal -->
    <div v-if="showReplyModal" class="modal-overlay" @click.self="showReplyModal = false">
      <div class="modal-panel" style="max-width:480px">
        <div class="modal-header">
          <h2>Reply to Comment</h2>
          <button class="modal-close" @click="closeReplyPostCommentModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="sc-field">
            <textarea v-model="edit_comment" placeholder="Write your reply..." rows="4"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="closeReplyPostCommentModal">Cancel</button>
          <button class="btn-primary" :disabled="loading" @click="replyProjectComment()">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Reply</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Edit project modal -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="cancelForm()">
      <div class="modal-panel">
        <div class="modal-header">
          <h2>Update Project Details</h2>
          <button class="modal-close" @click="cancelForm()">&times;</button>
        </div>
        <div class="modal-body">
          <div class="sc-field">
            <label>Project Name <span class="req">*</span></label>
            <input type="text" v-model="project.name" />
          </div>
          <div class="sc-field">
            <label>Project Description <span class="req">*</span></label>
            <QuillEditor v-model:content="project.description" contentType="html" :toolbar="customToolbar" />
          </div>

          <div class="form-section-label">Project Category</div>
          <div class="fields-row">
            <div class="sc-field">
              <label>Category</label>
              <select v-model="project.project_interest_1" @change="fectchInterestChildren($event)">
                <option value="" disabled>Select Category</option>
                <option v-for="(data, i) in interests" :key="i" :value="data.id">{{ data.name }}</option>
              </select>
            </div>
            <div v-if="interest_children.length" class="sc-field">
              <label>Sub-category</label>
              <select v-model="project.project_interest_2" @change="fectchInterestChildren2($event)">
                <option value="" disabled>Select</option>
                <option v-for="data in interest_children" :key="data.id" :value="data.id">{{ data.name }}</option>
              </select>
            </div>
            <div v-if="interest_children2.length" class="sc-field">
              <label>Sub-sub-category</label>
              <select v-model="project.project_interest_3">
                <option value="" disabled>Select</option>
                <option v-for="data in interest_children2" :key="data.id" :value="data.id">{{ data.name }}</option>
              </select>
            </div>
          </div>

          <div class="fields-row">
            <div class="sc-field">
              <label>Start Date</label>
              <input type="date" v-model="project.project_start_date" />
            </div>
            <div class="sc-field">
              <label>Status</label>
              <select v-model="project.status">
                <option value="" disabled>Select</option>
                <option value="1">Completed</option>
                <option value="2">On-going</option>
              </select>
            </div>
            <div v-if="project.status == 1" class="sc-field">
              <label>End Date</label>
              <input type="date" v-model="project.project_end_date" />
            </div>
          </div>

          <div class="form-section-label">Project Location</div>
          <div class="fields-row">
            <div class="sc-field">
              <label>Country</label>
              <select v-model="project.country_id" @change="fetchStates($event)">
                <option value="" disabled>Country</option>
                <option v-for="data in country" :key="data.id" :value="data.id">{{ data.name }}</option>
              </select>
            </div>
            <div class="sc-field">
              <label>State</label>
              <select v-model="project.state_id" @change="fetchCities($event)">
                <option value="" disabled>State</option>
                <option v-for="data in state" :key="data.id" :value="data.id">{{ data.name }}</option>
              </select>
            </div>
            <div class="sc-field">
              <label>City</label>
              <select v-model="project.city_id">
                <option value="" disabled>City</option>
                <option v-for="data in city" :key="data.id" :value="data.id">{{ data.name }}</option>
              </select>
            </div>
          </div>

          <div class="sc-field">
            <label>Address</label>
            <textarea v-model="project.address" rows="2"></textarea>
          </div>

          <div class="sc-field">
            <label>Height</label>
            <select v-model="project.height">
              <option value="" disabled>Select height</option>
              <option value="Low rise (Less than 3 Storeys)">Low rise (Less than 3 Storeys)</option>
              <option value="Mid rise (4 - 10 Storeys)">Mid rise (4 - 10 Storeys)</option>
              <option value="High rise (11 - 30 Storeys)">High rise (11 - 30 Storeys)</option>
              <option value="Skyscrappers (above 30 storeys)">Skyscrappers (above 30 storeys)</option>
            </select>
          </div>

          <div class="sc-field">
            <label>Project Client/Developer</label>
            <multiselect v-model="project.client" label="name" placeholder="Select one" :options="users" :allow-empty="true" />
          </div>
          <div class="sc-field">
            <label>Project Architect <button type="button" class="add-link" @click="addArchitectTeam">+ Add Team Member</button></label>
            <multiselect v-model="project.architect" label="name" placeholder="Select one" :options="users" :allow-empty="true" />
            <div v-for="(input, i) in details.architectteam" :key="'at'+i" class="team-input-row">
              <input type="text" :value="input.user.name" readonly />
              <button type="button" class="remove-btn" @click="deleteTeamMember(input.id)">Remove</button>
            </div>
            <div v-for="(input, i) in project.architect_team" :key="'nat'+i" class="team-input-row">
              <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="Search team" />
              <button type="button" class="remove-btn" @click="deleteArchitectTeam(i)">Remove</button>
            </div>
          </div>
          <div class="sc-field">
            <label>Structural Engineer <button type="button" class="add-link" @click="addStructuralEngineerTeam">+ Add Team Member</button></label>
            <multiselect v-model="project.structural_engineer" label="name" placeholder="Select one" :options="users" :allow-empty="true" />
            <div v-for="(input, i) in details.structuralengineerteam" :key="'st'+i" class="team-input-row">
              <input type="text" :value="input.user.name" readonly />
              <button type="button" class="remove-btn" @click="deleteTeamMember(input.id)">Remove</button>
            </div>
            <div v-for="(input, i) in project.structural_engineer_team" :key="'nst'+i" class="team-input-row">
              <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="Search team" />
              <button type="button" class="remove-btn" @click="deleteStructuralEngineerTeam(i)">Remove</button>
            </div>
          </div>
          <div class="sc-field">
            <label>MEP Engineer <button type="button" class="add-link" @click="addMepEngineerTeam">+ Add Team Member</button></label>
            <multiselect v-model="project.mep_engineer" label="name" placeholder="Select one" :options="users" :allow-empty="true" />
            <div v-for="(input, i) in project.mep_engineer_team" :key="'mep'+i" class="team-input-row">
              <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="Search team" />
              <button type="button" class="remove-btn" @click="deleteMepEngineerTeam(i)">Remove</button>
            </div>
          </div>
          <div class="sc-field">
            <label>Quantity Surveyor <button type="button" class="add-link" @click="addSurveyorTeam">+ Add Team Member</button></label>
            <multiselect v-model="project.quantity_surveyor" label="name" placeholder="Select one" :options="users" :allow-empty="true" />
            <div v-for="(input, i) in project.quantity_surveyor_team" :key="'qs'+i" class="team-input-row">
              <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="Search team" />
              <button type="button" class="remove-btn" @click="deleteSurveyorTeam(i)">Remove</button>
            </div>
          </div>
          <div class="sc-field">
            <label>Main Contractor <button type="button" class="add-link" @click="addMainContractorTeam">+ Add Team Member</button></label>
            <multiselect v-model="project.main_contractor" label="name" placeholder="Select one" :options="users" :allow-empty="true" />
            <div v-for="(input, i) in project.main_contractor_team" :key="'mc'+i" class="team-input-row">
              <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="Search team" />
              <button type="button" class="remove-btn" @click="deleteMainContractorTeam(i)">Remove</button>
            </div>
          </div>
          <div class="sc-field">
            <label>Mechanical Contractor <button type="button" class="add-link" @click="addMechanicalContractorTeam">+ Add Team Member</button></label>
            <multiselect v-model="project.mechanical_contractor" label="name" placeholder="Select one" :options="users" :allow-empty="true" />
            <div v-for="(input, i) in project.mechanical_contractor_team" :key="'mcc'+i" class="team-input-row">
              <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="Search team" />
              <button type="button" class="remove-btn" @click="deleteMechanicalContractorTeam(i)">Remove</button>
            </div>
          </div>
          <div class="sc-field">
            <label>Electrical Contractor <button type="button" class="add-link" @click="addElectricalContractorTeam">+ Add Team Member</button></label>
            <multiselect v-model="project.electrical_contractor" label="name" placeholder="Select one" :options="users" :allow-empty="true" />
            <div v-for="(input, i) in project.electrical_contractor_team" :key="'ec'+i" class="team-input-row">
              <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="Search team" />
              <button type="button" class="remove-btn" @click="deleteElectricalContractorTeam(i)">Remove</button>
            </div>
          </div>
          <div class="sc-field">
            <label>Piling Contractor <button type="button" class="add-link" @click="addPilingTeam">+ Add Team Member</button></label>
            <multiselect v-model="project.piling_contractor" label="name" placeholder="Select one" :options="users" :allow-empty="true" />
            <div v-for="(input, i) in project.piling_contractor_team" :key="'pc'+i" class="team-input-row">
              <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="Search team" />
              <button type="button" class="remove-btn" @click="deletePilingTeam(i)">Remove</button>
            </div>
          </div>
          <div class="sc-field">
            <label>Lift Contractor <button type="button" class="add-link" @click="addLiftTeam">+ Add Team Member</button></label>
            <multiselect v-model="project.lift_contractor" label="name" placeholder="Select one" :options="users" :allow-empty="true" />
            <div v-for="(input, i) in project.lift_contractor_team" :key="'lc'+i" class="team-input-row">
              <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="Search team" />
              <button type="button" class="remove-btn" @click="deleteLiftTeam(i)">Remove</button>
            </div>
          </div>
          <div class="sc-field">
            <label>Project Manager <button type="button" class="add-link" @click="addPMTeam">+ Add Team Member</button></label>
            <multiselect v-model="project.project_manager" label="name" placeholder="Select one" :options="users" :allow-empty="true" />
            <div v-for="(input, i) in project.project_manager_team" :key="'pm'+i" class="team-input-row">
              <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="Search team" />
              <button type="button" class="remove-btn" @click="deletePMTeam(i)">Remove</button>
            </div>
          </div>
          <div class="sc-field">
            <label>Vendors <button type="button" class="add-link" @click="addVendor">+ Add Vendor</button></label>
            <div v-for="(input, i) in details.vendors" :key="'ev'+i" class="team-input-row">
              <input type="text" :value="input.category ? input.category.name : ''" readonly />
              <input type="text" :value="input.user ? input.user.name : ''" readonly />
              <button type="button" class="remove-btn" @click="deleteVendorTeam(input.id)">Remove</button>
            </div>
            <div v-for="(input, i) in project.project_vendors" :key="'nv'+i" class="team-input-row">
              <model-list-select :list="vendor_category" v-model="input.vendor" option-value="id" option-text="name" placeholder="Category" />
              <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="User" />
              <button type="button" class="remove-btn" @click="deleteVendor(i)">Remove</button>
            </div>
          </div>

          <div class="sc-field">
            <label>YouTube Video URL</label>
            <input type="url" v-model="project.youtube_url" placeholder="https://www.youtube.com/watch?v=..." />
          </div>
          <div class="sc-field">
            <label>Instagram Video URL</label>
            <input type="url" v-model="project.instagram_url" placeholder="https://www.instagram.com/p/..." />
          </div>

          <!-- Existing images -->
          <div class="sc-field">
            <label>Project Images</label>
            <div v-if="details.images && details.images.length" class="existing-images">
              <div v-for="(data, i) in details.images" :key="i" class="existing-img-wrap" :class="{ 'existing-img-wrap--cover': data.is_cover }">
                <img :src="data.path" class="existing-img" :alt="'image '+i" />
                <button type="button" class="remove-img-btn" @click="deleteProjectImage(data.id)">&times;</button>
                <button
                  type="button"
                  class="cover-img-btn"
                  :class="{ 'cover-img-btn--active': data.is_cover }"
                  :title="data.is_cover ? 'Cover image' : 'Set as cover'"
                  @click="setCoverImage(data.id)"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" :fill="data.is_cover ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                </button>
              </div>
            </div>
            <vue-upload-multiple-image
              @upload-success="uploadImageSuccess"
              @before-remove="beforeRemove"
              @edit-image="editImage"
              @data-change="dataChange"
              :data-images="images"
              dragText="Drag and drop images"
              browseText="Browse Images"
              primaryText="added"
              markIsPrimaryText="Set default image"
              popupText="Description default image"
              :maxImage="5"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="cancelForm()">Cancel</button>
          <button class="btn-primary" :disabled="loading" @click="updateProject()">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Update Project</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Delete confirm dialog -->
    <transition name="del-fade">
      <div v-if="confirmDelete" class="del-overlay" @click.self="confirmDelete = false">
        <div class="del-dialog" role="dialog" aria-modal="true">
          <div class="del-dialog__icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            </svg>
          </div>
          <h3 class="del-dialog__title">Delete Project?</h3>
          <p class="del-dialog__body">
            This will permanently delete <strong>{{ details.name }}</strong>. This action cannot be undone.
          </p>
          <div class="del-dialog__actions">
            <button class="del-btn del-btn--cancel" @click="confirmDelete = false" :disabled="deleteLoading">Cancel</button>
            <button class="del-btn del-btn--confirm" @click="deleteProject" :disabled="deleteLoading">
              <span v-if="deleteLoading" class="del-spinner"></span>
              <span v-else>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

  </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout.vue';
import axios from 'axios';
import moment from 'moment';
import VueEasyLightbox from 'vue-easy-lightbox';
import VueUploadMultipleImage from '@/components/VueUploadMultipleImage';
import ModelListSelect from '@/components/ModelListSelect';
import Multiselect from 'vue-multiselect';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import InfiniteLoading from 'v3-infinite-loading';
import { useSEO } from '@/utils/seo.js';

export default {
  name: 'ProjectDetail',
  components: {
    AppLayout,
    VueEasyLightbox,
    VueUploadMultipleImage,
    ModelListSelect,
    Multiselect,
    QuillEditor,
    InfiniteLoading,
  },

  created() {
    this.fetchDetails();
    this.fetchCountries();
    this.fetchVendorCategory();
    this.fectchInterests();
    this.fetchUsers();
    this.fetchCompanySetting();
    this.fetchAdverts();
  },

  watch: {
    $route: {
      immediate: true,
      handler(to) {
        let subject = to.params.id.substring(0, to.params.id.length - 10);
        document.title = to.meta.title || 'Structurecity | Project - ' + subject.replace(/-/g, ' ');
      }
    },
  },

  data() {
    return {
      details: {},
      settings: {},
      related_project: [],
      user: {},
      images: [],
      country: [],
      state: [],
      city: [],
      users: [],
      vendor_category: [],
      interests: [],
      interest_children: [],
      interest_children2: [],
      connection: {
        vendor_category: '',
        connection_type: '',
        user_id: localStorage.getItem('user'),
        project_id: this.$route.params.id,
      },
      currentData: {},
      titleTeam: {},
      loading: false,
      id: localStorage.getItem('user'),
      project: {
        user_id: localStorage.getItem('user'),
        name: '',
        project_id: '',
        project_interest_1: '',
        project_interest_2: '',
        project_interest_3: '',
        description: '',
        project_start_date: '',
        project_end_date: '',
        country_id: 160,
        state_id: '',
        city_id: '',
        status: '',
        height: '',
        address: '',
        architect: '',
        structural_engineer: '',
        mep_engineer: '',
        client: '',
        quantity_surveyor: '',
        main_contractor: '',
        mechanical_contractor: '',
        electrical_contractor: '',
        piling_contractor: '',
        lift_contractor: '',
        project_manager: '',
        architect_team: [],
        structural_engineer_team: [],
        mep_engineer_team: [],
        quantity_surveyor_team: [],
        main_contractor_team: [],
        mechanical_contractor_team: [],
        electrical_contractor_team: [],
        piling_contractor_team: [],
        lift_contractor_team: [],
        project_manager_team: [],
        project_vendors: [],
        youtube_url: '',
        instagram_url: '',
        images: '',
      },
      visible: false,
      index: 0,
      activeImageIndex: 0,
      imgSrc: '',
      cropImg: '',
      image: '',
      modal: false,
      submitting: false,
      editmodal: false,
      imgs: '',
      imageSrc: '',
      croppedImageSrc: '',
      comment: { description: [] },
      newComment: '',
      adverts: [],
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ],
      page: 1,
      edit_comment: '',
      edit_comment_id: '',
      activeTab: 'overview',
      showEditModal: false,
      showConnectModal: false,
      confirmDelete: false,
      deleteLoading: false,
      likeLoading: false,
      showLikersModal: false,
      showTeamModal: false,
      showEditCommentModal: false,
      showReplyModal: false,
      successMsg: '',
      errorMsg: '',
    };
  },

  methods: {
    nameColor(name) {
      const palette = ['#4f46e5','#0891b2','#059669','#d97706','#7c3aed','#db2777','#ea580c','#0284c7'];
      let h = 0;
      for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
      return palette[Math.abs(h) % palette.length];
    },
    initAvatar(name, size = 40) {
      const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
      const bg = this.nameColor(name);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${size/2}" cy="${size/2}" r="${size/2}" fill="${bg}"/><text x="${size/2}" y="${size/2+5}" font-family="Arial,sans-serif" font-size="${Math.round(size*0.36)}" font-weight="700" fill="#fff" text-anchor="middle">${initials}</text></svg>`;
      return 'data:image/svg+xml;base64,' + btoa(svg);
    },

    logout() {
      localStorage.clear();
      this.$router.go();
    },

    setImage: function (e) {
      const file = e.target.files[0];
      if (!file.type.includes('image/')) {
        alert('Please select an image file');
        return;
      }
      if (typeof FileReader === 'function') {
        const reader = new FileReader();
        reader.onload = event => {
          this.imageSrc = event.target.result;
          this.$refs.cropper ? this.$refs.cropper.replace(event.target.result) : '';
        };
        reader.readAsDataURL(file);
      } else {
        alert('Sorry, FileReader API not supported');
      }
    },

    cropImage() {
      this.croppedImageSrc = this.$refs.cropper.getCroppedCanvas().toDataURL();
    },

    uploadImage(id) {
      var self = this;
      self.loading = true;
      this.$refs.cropper.getCroppedCanvas().toBlob(function (blob) {
        let formData = new FormData();
        formData.append('name', 'image-name-' + new Date().getTime());
        formData.append('file', blob);
        formData.append('id', id);
        axios.post(process.env.VUE_APP_API_URL + 'project/banner-update', formData)
          .then((response) => {
            if (response.status === 200) {
              self.successMsg = response.message;
              self.loading = false;
              window.Fire.emit('banner-action');
            } else {
              self.errorMsg = response.data.message;
              self.loading = false;
            }
          }).catch(() => { self.loading = false; });
      });
    },

    fetchDetails() {
      axios(process.env.VUE_APP_API_URL + 'project/detail/' + this.$route.params.id).then(response => {
        if (response.status !== 200) {
          return this.errorMessage = 'Could not fetch data';
        }
        this.details = response.data;
        const _img = response.data.images && response.data.images[0]
          ? response.data.images[0].path
          : null;
        useSEO({
          title: response.data.name,
          description: response.data.description,
          image: _img,
          url: window.location.href,
        });
        this.fetchCurrentStates(response.data.country_id);
        this.fetchCurrentCities(response.data.state_id);
        this.fetchRelatedProject(response.data.id);
        if (response.data.interests && response.data.interests[0]) {
          this.interestChildren(response.data.interests[0].interest_id);
        }
        if (response.data.interests && response.data.interests[1]) {
          this.interestChildren2(response.data.interests[1].interest_id);
        }
        this.project.project_id = response.data.id;
        this.project.name = response.data.name;
        this.project.description = response.data.description;
        this.project.project_start_date = response.data.project_start_date;
        this.project.project_end_date = response.data.project_end_date;
        this.project.country_id = response.data.country_id;
        this.project.state_id = response.data.state_id;
        this.project.city_id = response.data.city_id;
        this.project.status = response.data.status;
        this.project.height = response.data.height;
        this.project.address = response.data.address;
        this.project.architect = response.data.getarchitect;
        this.project.structural_engineer = response.data.structuralengineer;
        this.project.mep_engineer = response.data.mepengineer;
        this.project.piling_contractor = response.data.pilingcontractor;
        this.project.lift_contractor = response.data.liftcontractor;
        this.project.project_manager = response.data.projectmanager;
        this.project.client = response.data.getclient;
        this.project.quantity_surveyor = response.data.quantitysurveyor;
        this.project.main_contractor = response.data.maincontractor;
        this.project.mechanical_contractor = response.data.mechanicalcontractor;
        this.project.electrical_contractor = response.data.electricalcontractor;
        this.project.project_interest_1 = response.data.interests && response.data.interests[0] ? response.data.interests[0].interest_id : '';
        this.project.project_interest_2 = response.data.interests && response.data.interests[1] ? response.data.interests[1].interest_id : '';
        this.project.project_interest_3 = response.data.interests && response.data.interests[2] ? response.data.interests[2].interest_id : '';
        this.project.youtube_url = response.data.youtube_url || '';
        this.project.instagram_url = response.data.instagram_url || '';
      }).catch(() => {});
    },

    fetchUsers() {
      axios(process.env.VUE_APP_API_URL + 'users').then(response => {
        if (response.status !== 200) return;
        this.users = response.data;
      }).catch(() => {});
    },

    fetchRelatedProject(project_id) {
      axios(process.env.VUE_APP_API_URL + 'project/related/' + project_id).then(response => {
        if (response.status !== 200) return;
        this.related_project = response.data.data;
        this.page = this.page + 1;
      }).catch(() => {});
    },

    handleLoadMore($state) {
      axios(process.env.VUE_APP_API_URL + 'project/related/' + this.details.id + '?page=' + this.page)
        .then(res => {
          if (res.data.data) {
            res.data.data.forEach(value => this.related_project.push(value));
          }
          $state.loaded();
          this.page = this.page + 1;
          if (!res.data.data || res.data.data.length <= 0) {
            $state.complete();
          }
        }).catch(() => {});
    },

    fetchCompanySetting() {
      axios(process.env.VUE_APP_API_URL + 'company').then(response => {
        if (response.status !== 200) return;
        this.settings = response.data;
      }).catch(() => {});
    },

    fetchCountries() {
      axios(process.env.VUE_APP_API_URL + 'country').then(response => {
        this.country = response.data;
      }).catch(() => {});
    },

    fetchStates(event) {
      axios(process.env.VUE_APP_API_URL + 'state/' + event.target.value).then(response => {
        this.state = response.data;
      }).catch(() => {});
    },

    fetchCities(event) {
      axios(process.env.VUE_APP_API_URL + 'city/' + event.target.value).then(response => {
        this.city = response.data;
      }).catch(() => {});
    },

    fetchCurrentStates(id) {
      axios(process.env.VUE_APP_API_URL + 'state/' + id).then(response => {
        this.state = response.data;
      }).catch(() => {});
    },

    fetchCurrentCities(id) {
      axios(process.env.VUE_APP_API_URL + 'city/' + id).then(response => {
        this.city = response.data;
      }).catch(() => {});
    },

    fetchVendorCategory() {
      axios(process.env.VUE_APP_API_URL + 'vendor-category').then(response => {
        this.vendor_category = response.data;
      }).catch(() => {});
    },

    fectchInterests() {
      axios(process.env.VUE_APP_API_URL + 'interest').then(response => {
        this.interests = response.data;
      }).catch(() => {});
    },

    fectchInterestChildren(e) {
      axios(process.env.VUE_APP_API_URL + 'interest/' + e.target.value).then(response => {
        this.interest_children = response.data;
      }).catch(() => {});
    },

    fectchInterestChildren2(e) {
      axios(process.env.VUE_APP_API_URL + 'interest/' + e.target.value).then(response => {
        this.interest_children2 = response.data;
      }).catch(() => {});
    },

    interestChildren(id) {
      axios(process.env.VUE_APP_API_URL + 'interest/' + id).then(response => {
        this.interest_children = response.data;
      }).catch(() => {});
    },

    interestChildren2(id) {
      axios(process.env.VUE_APP_API_URL + 'interest/' + id).then(response => {
        this.interest_children2 = response.data;
      }).catch(() => {});
    },

    fetchAdverts() {
      axios(process.env.VUE_APP_API_URL + 'adverts').then(response => {
        this.adverts = response.data;
      }).catch(() => {});
    },

    updateProject() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'project/update', this.project)
        .then((response) => {
          if (response.status == 200) {
            this.successMsg = 'Project updated successfully';
            window.Fire.emit('delete-team-action');
            this.showEditModal = false;
            this.images = [];
            this.loading = false;
          } else {
            this.errorMsg = response.data.message;
            this.loading = false;
          }
        }).catch(() => { this.loading = false; });
    },

    cancelForm() {
      if (confirm('Are you sure you want to discard this form?')) {
        this.showEditModal = false;
        this.images = [];
      }
    },

    likeProject() {
      if (this.alreadyLiked || this.likeLoading) return;
      this.likeLoading = true;
      axios.post(process.env.VUE_APP_API_URL + 'project/like', {
        project_id: this.details.id,
        user_id: this.id,
      })
        .then(res => {
          if (res.status === 200) {
            if (!this.details.likes) this.details.likes = [];
            this.details.likes.push({ user_id: this.id });
            this.$toastr.s('Liked!');
          } else {
            this.$toastr.e(res.message || 'Could not like project.');
          }
          this.likeLoading = false;
        })
        .catch(() => {
          this.$toastr.e('Could not like project.');
          this.likeLoading = false;
        });
    },

    deleteProject() {
      this.deleteLoading = true;
      axios.delete(process.env.VUE_APP_API_URL + 'project/delete/' + this.details.id)
        .then(res => {
          if (res.status === 200 || res.status === 204) {
            this.$toastr.s('Project deleted successfully.');
            this.$router.push({ name: 'Home' });
          } else {
            this.$toastr.e(res.message || 'Could not delete project.');
            this.confirmDelete = false;
          }
          this.deleteLoading = false;
        })
        .catch(() => {
          this.$toastr.e('Could not delete project.');
          this.deleteLoading = false;
        });
    },

    follow(id) {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'user/follow', { user_id: id, followed_by: localStorage.getItem('user') })
        .then((response) => {
          if (response.status === 200) {
            this.successMsg = 'Followed successfully';
          }
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },

    connectProject() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'project/connect', this.connection)
        .then((response) => {
          if (response.status === 200) {
            this.successMsg = 'Connection request sent';
            this.showConnectModal = false;
            this.connection.vendor_category = '';
            this.connection.connection_type = '';
          } else {
            this.errorMsg = response.data.message;
          }
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },

    updateBanner() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'project/banner-update', {})
        .then((response) => {
          if (response.status === 200) {
            this.successMsg = 'Banner updated';
            window.Fire.emit('banner-action');
          }
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },

    deleteTeamMember(id) {
      axios(process.env.VUE_APP_API_URL + 'project/delete-team-member/' + id)
        .then((response) => {
          if (response.status === 200) {
            window.Fire.emit('delete-team-action');
          }
        }).catch(() => {});
    },

    deleteVendorTeam(id) {
      axios(process.env.VUE_APP_API_URL + 'project/delete-vendor-team/' + id)
        .then((response) => {
          if (response.status === 200) {
            window.Fire.emit('delete-team-action');
          }
        }).catch(() => {});
    },

    deleteProjectImage(id) {
      axios(process.env.VUE_APP_API_URL + 'project/delete-project-image/' + id)
        .then((response) => {
          if (response.status === 200) {
            window.Fire.emit('delete-team-action');
          }
        }).catch(() => {});
    },

    setCoverImage(imageId) {
      axios.post(process.env.VUE_APP_API_URL + 'project/set-cover-image', {
        project_id: this.details.id,
        image_id: imageId,
      }, { skipToast: true }).then(res => {
        if (res.status === 200) {
          this.details.images.forEach(img => { img.is_cover = img.id === imageId ? 1 : 0; });
        }
      }).catch(() => {});
    },

    viewTeam(event, title) {
      this.currentData = event;
      this.titleTeam = title;
      this.showTeamModal = true;
    },

    openUpdateProject() {
      this.showEditModal = true;
    },

    addArchitectTeam() { this.project.architect_team.push({ user_id: '' }); },
    deleteArchitectTeam(index) { this.project.architect_team.splice(index, 1); },
    addStructuralEngineerTeam() { this.project.structural_engineer_team.push({ user_id: '' }); },
    deleteStructuralEngineerTeam(index) { this.project.structural_engineer_team.splice(index, 1); },
    addMepEngineerTeam() { this.project.mep_engineer_team.push({ user_id: '' }); },
    deleteMepEngineerTeam(index) { this.project.mep_engineer_team.splice(index, 1); },
    addSurveyorTeam() { this.project.quantity_surveyor_team.push({ user_id: '' }); },
    deleteSurveyorTeam(index) { this.project.quantity_surveyor_team.splice(index, 1); },
    addMainContractorTeam() { this.project.main_contractor_team.push({ user_id: '' }); },
    deleteMainContractorTeam(index) { this.project.main_contractor_team.splice(index, 1); },
    addMechanicalContractorTeam() { this.project.mechanical_contractor_team.push({ user_id: '' }); },
    deleteMechanicalContractorTeam(index) { this.project.mechanical_contractor_team.splice(index, 1); },
    addElectricalContractorTeam() { this.project.electrical_contractor_team.push({ user_id: '' }); },
    deleteElectricalContractorTeam(index) { this.project.electrical_contractor_team.splice(index, 1); },
    addPilingTeam() { this.project.piling_contractor_team.push({ user_id: '' }); },
    deletePilingTeam(index) { this.project.piling_contractor_team.splice(index, 1); },
    addLiftTeam() { this.project.lift_contractor_team.push({ user_id: '' }); },
    deleteLiftTeam(index) { this.project.lift_contractor_team.splice(index, 1); },
    addPMTeam() { this.project.project_manager_team.push({ user_id: '' }); },
    deletePMTeam(index) { this.project.project_manager_team.splice(index, 1); },
    addVendor() { this.project.project_vendors.push({ vendor: '', user_id: '' }); },
    deleteVendor(index) { this.project.project_vendors.splice(index, 1); },

    uploadImageSuccess(formData, index, fileList) {
      this.project.images = fileList;
    },
    beforeRemove(index, done) {
      if (confirm('Remove image?')) done();
    },
    editImage() {},
    dataChange() {},

    formatDate(date) { return moment(date).fromNow(); },
    formatDate2(date) { return moment(date).format('DD MMMM, YYYY'); },

    showImg(index) {
      this.activeImageIndex = index;
      this.visible = true;
    },
    showSingle(data) {
      this.imgs = data;
      this.visible = true;
    },
    show() { this.visible = true; },
    handleHide() { this.visible = false; },

    forgotPassword() { window.location.href = '/forgot-password'; },
    register() { window.location.href = '/register'; },

    login() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'auth/login', {})
        .then((response) => {
          if (response.status === 200) {
            this.showConnectModal = false;
            localStorage.setItem('user', response.data.data.id);
            localStorage.setItem('slug', response.data.data.slug);
            this.$router.go();
          }
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },

    submitComment(event, id) {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'project/comment', { comment: event.target.value, project_id: id, user_id: localStorage.getItem('user') })
        .then((response) => {
          if (response.status === 200) {
            this.comment.description = [];
            window.Fire.emit('banner-action');
          }
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },

    submitComment2(comment, id) {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'project/comment', { comment: comment, project_id: id, user_id: localStorage.getItem('user') })
        .then((response) => {
          if (response.status === 200) {
            this.comment.description = [];
            this.newComment = '';
            window.Fire.emit('banner-action');
          }
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },

    sendSubmitComment(id) {
      this.submitComment2(this.newComment, id);
    },

    editProjectComment(comment_id, comment) {
      this.edit_comment = comment;
      this.edit_comment_id = comment_id;
      this.showEditCommentModal = true;
    },

    closeReplyPostCommentModal() {
      this.edit_comment_id = '';
      this.showReplyModal = false;
    },

    closeEditPostCommentModal() {
      this.edit_comment = '';
      this.edit_comment_id = '';
      this.showEditCommentModal = false;
    },

    editComment() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'project/comment/edit', { project_id: this.details.id, comment: this.edit_comment, comment_id: this.edit_comment_id, user_id: localStorage.getItem('user') })
        .then((response) => {
          if (response.status === 200) {
            this.edit_comment = '';
            this.edit_comment_id = '';
            this.showEditCommentModal = false;
            window.Fire.emit('banner-action');
          }
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },

    deleteComment(comment_id) {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'project/comment/delete', { project_id: this.details.id, comment_id: comment_id, user_id: localStorage.getItem('user') })
        .then((response) => {
          if (response.status === 200) {
            this.edit_comment = '';
            this.edit_comment_id = '';
            window.Fire.emit('banner-action');
          }
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },

    likeProjectComment(comment_id) {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'project/comment/like', { comment_id: comment_id, user_id: localStorage.getItem('user') })
        .then((response) => {
          if (response.status === 200) {
            window.Fire.emit('banner-action');
          }
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },

    unlikeProjectComment(comment_id) {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'project/comment/unlike', { comment_id: comment_id, user_id: localStorage.getItem('user') })
        .then((response) => {
          if (response.status === 200) {
            window.Fire.emit('banner-action');
          }
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },

    checkIfUser(source) {
      if (!source || source.length === 0) return false;
      for (var i = 0; i < source.length; i++) {
        if (source[i].user_id == this.id) return true;
      }
      return false;
    },

    replyProjectCommentModal(comment_id) {
      this.edit_comment_id = comment_id;
      this.showReplyModal = true;
    },

    replyProjectComment() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'project/comment/reply', { project_id: this.details.id, comment: this.edit_comment, parent_id: this.edit_comment_id, user_id: localStorage.getItem('user') })
        .then((response) => {
          if (response.status === 200) {
            this.edit_comment = '';
            this.edit_comment_id = '';
            this.showReplyModal = false;
            window.Fire.emit('banner-action');
          }
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },

    closeTeamModal() { this.showTeamModal = false; },
    closeTeamConnectModal() { this.showConnectModal = false; },
  },

  mounted() {
    window.Fire.on('banner-action', this.fetchDetails);
    window.Fire.on('delete-team-action', this.fetchDetails);
  },

  beforeUnmount() {
    window.Fire.off('banner-action', this.fetchDetails);
    window.Fire.off('delete-team-action', this.fetchDetails);
  },

  computed: {
    image_url() { return process.env.VUE_APP_IMAGE_URL; },
    alreadyLiked() {
      if (!this.id || !this.details.likes) return false;
      return this.details.likes.some(l => String(l.user_id) === String(this.id));
    },
    youtubeVideoId() {
      const url = this.details && this.details.youtube_url;
      if (!url) return '';
      const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
      return m ? m[1] : '';
    },
    instagramShortcode() {
      const url = this.details && this.details.instagram_url;
      if (!url) return '';
      const m = url.match(/instagram\.com\/(?:p|reel)\/([A-Za-z0-9_-]+)/);
      return m ? m[1] : '';
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.sc-video-wrap { position: relative; width: 100%; padding-top: 56.25%; border-radius: 8px; overflow: hidden; background: #000; }
.sc-video-wrap--instagram { padding-top: 130%; max-width: 540px; background: #fafafa; }
.sc-video-frame { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; }

.detail-layout {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 2rem;
  padding: 2.5rem 0;
  align-items: start;
}
.detail-main { display: flex; flex-direction: column; gap: 1.5rem; }
.detail-aside { display: flex; flex-direction: column; gap: 1.25rem; position: sticky; top: 80px; }

.sc-card { background: #fff; border-radius: var(--radius-lg); border: 1px solid var(--border-light); box-shadow: var(--shadow-sm); }
.sc-card-header { padding: 1rem 1.25rem; border-bottom: 1px solid var(--border-light); font-weight: 700; font-size: 1rem; color: var(--text); }
.sc-card-body { padding: 1.25rem; }

.title-bar { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.title-bar-left { flex: 1; }
.title-bar-right { display: flex; gap: .5rem; flex-shrink: 0; }
.detail-title { font-size: 1.5rem; font-weight: 800; color: var(--text); margin: 0 0 .5rem; line-height: 1.3; }
.detail-meta { display: flex; align-items: center; gap: .75rem; flex-wrap: wrap; font-size: .875rem; color: var(--text-muted); }
.meta-loc { display: flex; align-items: center; gap: .25rem; }

.status-badge { display: inline-block; padding: .2rem .6rem; border-radius: var(--radius-full); font-size: .75rem; font-weight: 600; }
.badge-success { background: #dcfce7; color: #166534; }
.badge-warning { background: #fef9c3; color: #854d0e; }

.gallery-hero { position: relative; aspect-ratio: 16/9; border-radius: var(--radius-lg); overflow: hidden; background: var(--surface-3); cursor: pointer; }
.gallery-hero img { width: 100%; height: 100%; object-fit: cover; transition: transform .3s; }
.gallery-hero:hover img { transform: scale(1.02); }
.gallery-overlay-hint { position: absolute; bottom: .75rem; right: .75rem; background: rgba(0,0,0,.55); color: #fff; font-size: .75rem; padding: .25rem .6rem; border-radius: var(--radius-full); }
.gallery-thumbs { display: flex; gap: .5rem; margin-top: .5rem; overflow-x: auto; padding-bottom: .25rem; }
.gallery-thumb { width: 80px; height: 56px; border-radius: .5rem; overflow: hidden; flex-shrink: 0; cursor: pointer; border: 2px solid transparent; transition: border-color .15s; }
.gallery-thumb.active { border-color: var(--primary); }
.gallery-thumb img { width: 100%; height: 100%; object-fit: cover; }
.gallery-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .5rem; aspect-ratio: 16/7; border-radius: var(--radius-lg); background: var(--surface-3); color: var(--text-muted); border: 1.5px dashed var(--border); }

.filter-tabs { display: flex; gap: .25rem; padding: .75rem 1rem; }
.filter-tab { padding: .5rem 1.125rem; border: none; border-radius: var(--radius-full); background: var(--surface-3); color: var(--text-muted); font-size: .875rem; font-weight: 500; cursor: pointer; font-family: inherit; transition: all .15s; }
.filter-tab.active { background: var(--primary); color: #fff; }
.filter-tab:hover:not(.active) { background: var(--surface-2); color: var(--text); }

.details-grid { display: flex; flex-direction: column; }
.detail-row { display: flex; padding: .75rem 0; border-bottom: 1px solid var(--border-light); gap: 1rem; }
.detail-row:last-child { border-bottom: none; }
.detail-label { font-weight: 600; font-size: .875rem; color: var(--text-muted); min-width: 140px; flex-shrink: 0; }
.detail-value { font-size: .9375rem; color: var(--text); }

.prose-content { font-size: .9375rem; line-height: 1.7; color: var(--text); }

.team-member-row { display: flex; align-items: center; gap: 1rem; padding: .875rem 0; border-bottom: 1px solid var(--border-light); flex-wrap: wrap; }
.team-member-row:last-child { border-bottom: none; }
.team-role { font-weight: 600; font-size: .8125rem; color: var(--text-muted); min-width: 150px; flex-shrink: 0; }
.team-person { display: flex; align-items: center; gap: .5rem; flex: 1; }
.team-avatar { width: 36px; height: 36px; border-radius: 50%; object-fit: cover; }
.team-name { font-weight: 600; font-size: .875rem; color: var(--primary); }
.team-sub { font-size: .8125rem; color: var(--text-muted); }
.team-overlap { display: flex; align-items: center; gap: .25rem; cursor: pointer; }
.team-overlap-img { width: 28px; height: 28px; border-radius: 50%; border: 2px solid #fff; object-fit: cover; margin-left: -8px; }
.team-overlap-img:first-child { margin-left: 0; }
.team-count { font-size: .8125rem; color: var(--text-muted); margin-left: .375rem; }

.comment-compose { display: flex; flex-direction: column; gap: .75rem; margin-bottom: 1.5rem; padding-bottom: 1.5rem; border-bottom: 1px solid var(--border-light); }
.comment-textarea { width: 100%; padding: .75rem 1rem; border: 1.5px solid var(--border); border-radius: var(--radius); font-family: inherit; font-size: .9375rem; resize: vertical; outline: none; transition: border-color .15s; }
.comment-textarea:focus { border-color: var(--primary); }

.comment-item { display: flex; gap: .875rem; padding: 1rem 0; border-bottom: 1px solid var(--border-light); }
.comment-item:last-child { border-bottom: none; }
.comment-avatar { width: 36px; height: 36px; border-radius: 50%; background: var(--surface-3); overflow: hidden; flex-shrink: 0; }
.comment-avatar img { width: 100%; height: 100%; object-fit: cover; }
.comment-body { flex: 1; }
.comment-header-row { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; margin-bottom: .25rem; }
.comment-author { font-weight: 600; font-size: .875rem; color: var(--text); text-decoration: none; }
.comment-date { font-size: .8125rem; color: var(--text-muted); margin-left: auto; }
.comment-text { font-size: .9rem; color: var(--text); margin: .25rem 0; line-height: 1.6; }
.comment-actions { display: flex; gap: .75rem; }
.comment-action-btn { background: none; border: none; cursor: pointer; font-size: .8125rem; color: var(--text-muted); font-family: inherit; display: flex; align-items: center; gap: .25rem; padding: 0; transition: color .15s; }
.comment-action-btn:hover { color: var(--primary); }
.comment-action-btn.danger:hover { color: var(--danger); }
.comment-actions-menu { display: flex; gap: .375rem; margin-left: auto; }
.replies-list { margin-top: .75rem; padding-left: .875rem; border-left: 2px solid var(--border-light); }
.reply-item { padding: .75rem 0; }

.author-card { background: #fff; border-radius: var(--radius-lg); border: 1px solid var(--border-light); box-shadow: var(--shadow-sm); padding: 1.25rem; display: flex; flex-direction: column; align-items: center; text-align: center; gap: .75rem; }
.author-avatar { width: 72px; height: 72px; border-radius: 50%; overflow: hidden; background: var(--surface-3); }
.author-avatar img { width: 100%; height: 100%; object-fit: cover; }
.author-name { font-weight: 700; font-size: 1rem; color: var(--text); }
.author-role { font-size: .875rem; color: var(--text-muted); }
.author-actions { display: flex; gap: .5rem; width: 100%; }
.author-actions a { flex: 1; text-align: center; }

.vendor-row { display: flex; align-items: center; gap: .75rem; padding: .5rem 0; border-bottom: 1px solid var(--border-light); }
.vendor-row:last-child { border-bottom: none; }
.vendor-avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; }
.vendor-name { font-size: .875rem; font-weight: 600; color: var(--text); }
.vendor-cat { font-size: .8125rem; color: var(--text-muted); }

.ad-card { margin-bottom: .75rem; }
.ad-card:last-child { margin-bottom: 0; }
.ad-img { width: 100%; border-radius: var(--radius); }

.section-heading { font-size: 1.125rem; font-weight: 700; color: var(--text); margin-bottom: 1rem; }
.related-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }
.listing-title { font-size: .9375rem; font-weight: 600; color: var(--text); margin: .5rem 0 0; }
.listing-card-img { aspect-ratio: 4/3; overflow: hidden; border-radius: var(--radius); background: var(--surface-3); }
.listing-card-img img { width: 100%; height: 100%; object-fit: cover; }
.tag-badge { display: inline-block; padding: .15rem .5rem; background: var(--surface-3); border-radius: var(--radius-full); font-size: .75rem; font-weight: 500; color: var(--text-muted); }

.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 500; display: flex; align-items: flex-start; justify-content: center; padding: 2rem 1rem; overflow-y: auto; }
.modal-panel { background: #fff; border-radius: var(--radius-lg); box-shadow: var(--shadow-xl); width: 100%; max-width: 760px; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--border-light); }
.modal-header h2 { font-size: 1.25rem; font-weight: 700; margin: 0; }
.modal-close { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: var(--text-muted); line-height: 1; padding: 0; }
.modal-body { padding: 1.5rem; max-height: 70vh; overflow-y: auto; }
.modal-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--border-light); display: flex; justify-content: flex-end; gap: .75rem; }

.sc-field { display: flex; flex-direction: column; gap: .375rem; margin-bottom: 1rem; }
.sc-field label { font-size: .875rem; font-weight: 600; color: #334155; }
.sc-field input, .sc-field select, .sc-field textarea { width: 100%; padding: .625rem .875rem; border: 1.5px solid var(--border); border-radius: var(--radius); font-family: inherit; font-size: .9375rem; color: var(--text); background: #fff; outline: none; transition: border-color .15s; box-sizing: border-box; }
.sc-field input:focus, .sc-field select:focus, .sc-field textarea:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(30,58,95,.1); }
.form-section-label { font-size: .8125rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: var(--text-muted); margin: 1rem 0 .5rem; }
.fields-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; }
.req { color: var(--danger); }
.add-link { background: none; border: none; cursor: pointer; font-size: .8125rem; color: var(--primary); font-family: inherit; padding: 0; margin-left: .5rem; }
.team-input-row { display: flex; gap: .5rem; margin-top: .5rem; align-items: center; }
.remove-btn { background: none; border: 1px solid var(--danger); color: var(--danger); border-radius: var(--radius); padding: .25rem .5rem; font-size: .8125rem; cursor: pointer; flex-shrink: 0; }
.existing-images { display: flex; flex-wrap: wrap; gap: .75rem; margin-bottom: .75rem; }
.existing-img-wrap { position: relative; }
.existing-img { width: 72px; height: 72px; object-fit: cover; border-radius: var(--radius); }
.remove-img-btn { position: absolute; top: -6px; right: -6px; width: 20px; height: 20px; border-radius: 50%; background: var(--danger); color: #fff; border: none; cursor: pointer; font-size: .875rem; display: flex; align-items: center; justify-content: center; }
.cover-img-btn { position: absolute; bottom: -6px; left: -6px; width: 22px; height: 22px; border-radius: 50%; background: #fff; border: 1.5px solid #d1d5db; color: #9ca3af; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: border-color .15s, color .15s; }
.cover-img-btn:hover { border-color: #f59e0b; color: #f59e0b; }
.cover-img-btn--active { border-color: #f59e0b; color: #f59e0b; background: #fffbeb; }
.existing-img-wrap--cover .existing-img { outline: 2px solid #f59e0b; outline-offset: 2px; }

.team-modal-row { display: flex; align-items: center; gap: .75rem; padding: .75rem 0; border-bottom: 1px solid var(--border-light); }
.team-modal-row:last-child { border-bottom: none; }

.sc-alert { padding: .875rem 1.25rem; border-radius: var(--radius); margin-bottom: 1rem; font-size: .9375rem; }
.sc-alert-success { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
.sc-alert-error { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }

.no-content { color: var(--text-muted); font-size: .9375rem; text-align: center; padding: 2rem 0; }

.sc-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.infinite-wrapper { display: flex; justify-content: center; padding: 1.5rem 0; }

@media (max-width: 768px) {
  .detail-layout { grid-template-columns: 1fr; }
  .detail-aside { position: static; }
  .related-grid { grid-template-columns: 1fr 1fr; }
  .fields-row { grid-template-columns: 1fr; }
  .title-bar { flex-direction: column; }
}
:deep(.ql-editor) { min-height: 160px; }

.btn-danger {
  display: inline-flex; align-items: center; gap: .375rem;
  padding: .5rem 1rem; border-radius: .5rem; border: none; cursor: pointer;
  background: #fee2e2; color: #dc2626;
  font-size: .875rem; font-weight: 600;
  transition: background .15s;
}
.btn-danger:hover { background: #fecaca; }

.del-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.del-dialog {
  background: var(--surface, #fff);
  border-radius: 1rem;
  box-shadow: 0 20px 60px rgba(0,0,0,.2);
  padding: 2rem 1.75rem 1.5rem;
  width: 100%; max-width: 380px;
  text-align: center;
  animation: del-pop .18s ease;
}
@keyframes del-pop {
  from { opacity: 0; transform: scale(.93); }
  to   { opacity: 1; transform: scale(1); }
}
.del-dialog__icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 60px; height: 60px; border-radius: 50%;
  background: #fee2e2; color: #dc2626;
  margin-bottom: 1rem;
}
.del-dialog__title { font-size: 1.125rem; font-weight: 700; color: var(--text, #111); margin: 0 0 .5rem; }
.del-dialog__body { font-size: .9rem; color: var(--text-muted, #6b7280); line-height: 1.5; margin: 0 0 1.5rem; }
.del-dialog__body strong { color: var(--text, #111); }
.del-dialog__actions { display: flex; gap: .75rem; justify-content: center; }
.del-btn {
  flex: 1; padding: .6rem 1rem; border-radius: .625rem;
  font-size: .9rem; font-weight: 600; cursor: pointer; border: none;
  transition: background .15s, opacity .15s;
  display: flex; align-items: center; justify-content: center; gap: .5rem;
}
.del-btn:disabled { opacity: .6; cursor: not-allowed; }
.del-btn--cancel { background: var(--surface-2, #f3f4f6); color: var(--text, #111); }
.del-btn--cancel:hover:not(:disabled) { background: var(--border-light, #e5e7eb); }
.del-btn--confirm { background: #dc2626; color: #fff; }
.del-btn--confirm:hover:not(:disabled) { background: #b91c1c; }
.del-spinner {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.4); border-top-color: #fff;
  animation: spin .6s linear infinite; display: inline-block;
}
.del-fade-enter-active, .del-fade-leave-active { transition: opacity .18s; }
.del-fade-enter-from, .del-fade-leave-to { opacity: 0; }

.pd-tabs-bar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.pd-tabs-bar .filter-tabs { flex: 1; border-bottom: none; }
.pd-tab-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 4px; margin-left: 5px;
  border-radius: 9px; background: var(--primary, #1e3a5f); color: #fff;
  font-size: .7rem; font-weight: 700; line-height: 1;
}
.pd-actions-bar { display: flex; align-items: center; gap: .5rem; padding-right: .5rem; flex-shrink: 0; }
.pd-like-btn {
  display: inline-flex; align-items: center; gap: .375rem;
  padding: .4rem .85rem; border-radius: var(--radius-full, 999px);
  border: 1.5px solid var(--border-light, #e5e7eb);
  background: none; cursor: pointer; font-size: .875rem; font-weight: 600;
  color: var(--text-muted, #6b7280);
  transition: background .15s, color .15s, border-color .15s;
}
.pd-like-btn:hover:not(:disabled) { background: #fee2e2; color: #dc2626; border-color: #fca5a5; }
.pd-like-btn--liked { background: #fee2e2; color: #dc2626; border-color: #fca5a5; cursor: default; }
.pd-like-btn:disabled:not(.pd-like-btn--liked) { opacity: .6; cursor: not-allowed; }

/* Likers */
.pd-likes-wrap { display: flex; align-items: center; gap: .5rem; }
.pd-likers-btn { display: flex; align-items: center; gap: .375rem; background: none; border: none; cursor: pointer; padding: .25rem; border-radius: .5rem; transition: background .12s; }
.pd-likers-btn:hover { background: var(--surface-3, #f1f5f9); }
.pd-likers-stack { display: flex; }
.pd-likers-avatar { width: 24px; height: 24px; border-radius: 50%; object-fit: cover; border: 2px solid #fff; margin-left: -6px; }
.pd-likers-stack .pd-likers-avatar:first-child { margin-left: 0; }
.pd-likers-more { font-size: .75rem; font-weight: 600; color: var(--text-muted, #6b7280); }
.likers-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 800; display: flex; align-items: center; justify-content: center; padding: 1rem; }
.likers-modal { background: #fff; border-radius: 1rem; width: 100%; max-width: 380px; max-height: 70vh; display: flex; flex-direction: column; box-shadow: 0 20px 48px rgba(0,0,0,.18); }
.likers-modal__header { display: flex; align-items: center; justify-content: space-between; padding: .875rem 1rem; border-bottom: 1px solid var(--border-light, #f3f4f6); font-weight: 700; font-size: .9375rem; }
.likers-modal__close { background: none; border: none; cursor: pointer; color: var(--text-muted, #6b7280); display: flex; padding: .25rem; border-radius: .375rem; transition: background .12s; }
.likers-modal__close:hover { background: var(--surface-2, #f8fafc); }
.likers-modal__body { overflow-y: auto; padding: .5rem 0; }
.likers-item { display: flex; align-items: center; gap: .75rem; padding: .625rem 1rem; text-decoration: none; transition: background .12s; }
.likers-item:hover { background: var(--surface-2, #f8fafc); }
.likers-item__avatar { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.likers-item__info { display: flex; flex-direction: column; min-width: 0; }
.likers-item__name { font-size: .875rem; font-weight: 600; color: var(--text, #111827); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.likers-item__role { font-size: .75rem; color: var(--text-muted, #6b7280); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
